import { useState } from 'react';
import { withAppHOC } from '../../hoc';
import {
  StyledContentContainer,
  StyledMainAreaContainer,
  Section,
  SectionTitle,
} from '../../components/styled';
import PageHeader from '../../components/PageHeader/PageHeader';
import {
  Form,
  Fieldset,
  Label,
  MessageBox,
  Button,
  TextInput,
  OTPInput,
  Dropdown,
} from '../../components/FormElements';
import API from '../../api';
import { RESPONSE_STATUSES } from '../../constants';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationCircle,
  faIdCard,
  faUser,
  faReceipt,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import styled, { keyframes } from 'styled-components';
import { getAuthData } from '../../utils';
import { Heading1 } from '../../components/Headings';

// --------------------- STYLED COMPONENTS ---------------------
const StyledContent = styled.p`
  color: green;
  text-transform: uppercase;
  margin: 5px 0;
  animation: fadeIn 0.5s ease-in-out;
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StyledKYCPendingMessageContainer = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 15px;
  margin: 50px 0;
  svg {
    font-size: 60px;
    color: #0095ff;
  }
  h1 {
    margin: 0;
    padding: 0;
  }
  p {
    font-size: 14px;
    color: #222;
    margin: 0;
    padding: 0;
  }
`;

const MultiColumnContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CardSection = styled(Section)`
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  position: relative;
  background-color: #fafafa;
`;

const SectionIcon = styled(FontAwesomeIcon)`
  position: absolute;
  top: 20px;
  right: 20px;
  color: #0095ff;
  font-size: 24px;
`;

// --------------------- MAIN COMPONENT ---------------------
const KYCPending = () => {
  const {
    userProfile: {
      gstWalletStatus,
      gstInvoiceP2AStatus,
      gstInvoiceP2PStatus,
      kycStatus,
      kycRemark,
      firmType,
    },
  } = getAuthData();

  const gstTypeOptions = [{ name: '-- SELECT GST TYPE --', value: '' }];
  if (gstInvoiceP2PStatus === 'Active') gstTypeOptions.push({ name: 'P2P', value: 'P2P' });
  if (gstInvoiceP2AStatus === 'Active') gstTypeOptions.push({ name: 'P2A', value: 'P2A' });

  const [state, setState] = useState({
    isLoading: false,
    responseStatus: '',
    responseMessage: '',
    aadhaarNumber: '',
    aadhaarName: '',
    aadhaarRequestToken: '',
    aadhaarOTP: '',
    aadhaarStage: 'REQUEST',
    aadhaarFile: '',
    panNumber: '',
    panName: '',
    panFile: '',
    isGSTSeller: firmType === '7' ? 'No' : 'Yes',
    gstNumber: '',
    gstName: '',
    gstFile: '',
    gstType: 'P2P',
  });

  // --------------------- API HANDLERS ---------------------
  const handleApiCall = async (endpoint, payload, successCallback) => {
    setState((prev) => ({ ...prev, isLoading: true, responseStatus: '', responseMessage: '' }));
    try {
      const response = await API.post(endpoint, payload);
      const { status, message, data, actualData } = response.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        successCallback(data, actualData);
        setState((prev) => ({ ...prev, responseStatus: RESPONSE_STATUSES.SUCCESS, responseMessage: message }));
        setTimeout(() => setState((prev) => ({ ...prev, responseStatus: '', responseMessage: '' })), 3000);
      } else {
        setState((prev) => ({ ...prev, responseStatus: RESPONSE_STATUSES.FAILED, responseMessage: message }));
      }
    } catch (error) {
      setState((prev) => ({ ...prev, responseStatus: RESPONSE_STATUSES.FAILED, responseMessage: error.message }));
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const getAadhaarOTP = (e) => {
    e.preventDefault();
    handleApiCall('/authentication/fetch-aadhaar-otp', { aadhaarNumber: state.aadhaarNumber }, (data) => {
      setState((prev) => ({ ...prev, aadhaarRequestToken: data, aadhaarStage: 'OTP' }));
    });
  };

  const fetchAadhaar = (e) => {
    e.preventDefault();
    handleApiCall(
      '/authentication/fetch-aadhaar',
      {
        aadhaarNumber: state.aadhaarNumber,
        aadhaarOtp: state.aadhaarOTP,
        aadhaarRequestToken: state.aadhaarRequestToken,
      },
      (data, actualData) => {
        setState((prev) => ({ ...prev, aadhaarName: data, aadhaarFile: actualData, aadhaarStage: 'SUCCESS' }));
      }
    );
  };

  const fetchPan = (e) => {
    e.preventDefault();
    handleApiCall('/authentication/fetch-pan', { panNumber: state.panNumber }, (data, actualData) => {
      setState((prev) => ({ ...prev, panName: data, panFile: actualData }));
    });
  };

  const fetchGst = (e) => {
    e.preventDefault();
    handleApiCall('/authentication/fetch-gst', { gstNumber: state.gstNumber }, (data, actualData) => {
      setState((prev) => ({ ...prev, gstName: data, gstFile: actualData }));
    });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    handleApiCall(
      '/authentication/kyc',
      {
        panName: state.panName,
        panNumber: state.panNumber,
        panFile: JSON.stringify(state.panFile),
        aadhaarName: state.aadhaarName,
        aadhaarNumber: state.aadhaarNumber,
        aadhaarFile: JSON.stringify(state.aadhaarFile),
        isGSTSeller: state.isGSTSeller,
        gstName: state.isGSTSeller === 'Yes' ? state.gstName : '',
        gstNumber: state.isGSTSeller === 'Yes' ? state.gstNumber : '',
        gstFile: state.isGSTSeller === 'Yes' ? JSON.stringify(state.gstFile) : '',
        gstType: state.gstType,
      },
      () => setTimeout(() => window.location.reload(), 3000)
    );
  };

  // --------------------- RENDER ---------------------
  if (kycStatus === 'Submitted') {
    return (
      <StyledKYCPendingMessageContainer>
        <FontAwesomeIcon icon={faExclamationCircle} />
        <Heading1>Pending KYC Verification</Heading1>
        <p>Your KYC information is submitted for verification.</p>
      </StyledKYCPendingMessageContainer>
    );
  }

  return (
    <>
      <PageHeader title="KYC Pending" />
      <StyledContentContainer>
        <StyledMainAreaContainer>
          <Form action="#" method="POST" onSubmit={onSubmit} style={{ maxWidth: '900px', margin: '0 auto' }}>
            {!isEmpty(kycRemark) && kycStatus === 'Rejected' && (
              <Fieldset>
                <MessageBox status="Rejected" message={`Rejected Reason : ${kycRemark}`} />
              </Fieldset>
            )}

            <MultiColumnContainer>
              {/* --------------------- PAN Section --------------------- */}
              <CardSection>
                <SectionTitle>PAN Details</SectionTitle>
                <SectionIcon icon={faIdCard} />
                <Fieldset>
                  <Label required>PAN Number</Label>
                  <TextInput
                    value={state.panNumber}
                    onChange={(value) => setState((prev) => ({ ...prev, panName: '', panFile: '', panNumber: value }))}
                    placeholder="PAN Number"
                    disabled={state.isLoading}
                  />
                </Fieldset>
                {state.panName && (
                  <Fieldset>
                    <Label>Name on PAN</Label>
                    <StyledContent>{state.panName}</StyledContent>
                  </Fieldset>
                )}
                <Fieldset>
                  <Button onClick={fetchPan} disabled={state.panName || state.isLoading}>
                    {state.isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Fetch Details'}
                  </Button>
                </Fieldset>
              </CardSection>

              {/* --------------------- Aadhaar Section --------------------- */}
              <CardSection>
                <SectionTitle>Aadhaar Details</SectionTitle>
                <SectionIcon icon={faUser} />
                <Fieldset>
                  <Label required>Aadhaar Number</Label>
                  <TextInput
                    value={state.aadhaarNumber}
                    onChange={(value) =>
                      setState((prev) => ({
                        ...prev,
                        aadhaarNumber: value,
                        aadhaarName: '',
                        aadhaarFile: '',
                        aadhaarRequestToken: '',
                        aadhaarOTP: '',
                        aadhaarStage: 'REQUEST',
                      }))
                    }
                    placeholder="Aadhaar Number"
                    disabled={state.isLoading || state.aadhaarStage === 'OTP'}
                  />
                </Fieldset>
                {state.aadhaarName && (
                  <Fieldset>
                    <Label>Name on Aadhaar</Label>
                    <StyledContent>{state.aadhaarName}</StyledContent>
                  </Fieldset>
                )}
                {state.aadhaarStage !== 'OTP' ? (
                  <Fieldset>
                    <Button onClick={getAadhaarOTP} disabled={state.aadhaarName || state.isLoading}>
                      {state.isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Send OTP'}
                    </Button>
                  </Fieldset>
                ) : (
                  <>
                    <Fieldset>
                      <Label required>Aadhaar OTP</Label>
                      <OTPInput
                        value={state.aadhaarOTP}
                        onChange={(value) => setState((prev) => ({ ...prev, aadhaarOTP: value }))}
                        placeholder="OTP"
                        disabled={state.isLoading}
                      />
                    </Fieldset>
                    <Fieldset>
                      <Button onClick={fetchAadhaar} disabled={state.isLoading}>
                        {state.isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Fetch Aadhaar'}
                      </Button>
                    </Fieldset>
                  </>
                )}
              </CardSection>
            </MultiColumnContainer>

            {/* --------------------- GST Section --------------------- */}
            {firmType !== '7' && gstWalletStatus === 'Active' && (
              <CardSection>
                <SectionTitle>GST Details</SectionTitle>
                <SectionIcon icon={faReceipt} />
                <Fieldset>
                  <Label required>Do you have GST?</Label>
                  <Dropdown
                    value={state.isGSTSeller}
                    onChange={(value) =>
                      setState((prev) => ({
                        ...prev,
                        isGSTSeller: value,
                        gstNumber: '',
                        gstName: '',
                        gstFile: '',
                      }))
                    }
                    placeholder="GST Status"
                    disabled={state.isLoading}
                    options={[
                      { name: '-- SELECT GST STATUS --', value: '' },
                      { name: 'Yes', value: 'Yes' },
                      { name: 'No', value: 'No' },
                    ]}
                  />
                </Fieldset>

                {state.isGSTSeller === 'Yes' && (
                  <>
                    <Fieldset>
                      <Label required>GST Number</Label>
                      <TextInput
                        value={state.gstNumber}
                        onChange={(value) => setState((prev) => ({ ...prev, gstNumber: value }))}
                        placeholder="GST Number"
                        disabled={state.isLoading}
                      />
                    </Fieldset>
                    {state.gstName && (
                      <Fieldset>
                        <Label>Name on GST</Label>
                        <StyledContent>{state.gstName}</StyledContent>
                      </Fieldset>
                    )}
                    <Fieldset>
                      <Button onClick={fetchGst} disabled={state.gstName || state.isLoading}>
                        {state.isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Fetch Details'}
                      </Button>
                    </Fieldset>
                    <Fieldset>
                      <Label required>GST Type</Label>
                      <Dropdown
                        value={state.gstType}
                        onChange={(value) => setState((prev) => ({ ...prev, gstType: value }))}
                        placeholder="GST Type"
                        disabled={state.isLoading}
                        options={gstTypeOptions}
                      />
                    </Fieldset>
                  </>
                )}
              </CardSection>
            )}

            {state.responseStatus && state.responseMessage && (
              <Fieldset>
                <MessageBox status={state.responseStatus} message={state.responseMessage} />
              </Fieldset>
            )}

            <Fieldset>
              <Button disabled={state.isLoading}>
                {state.isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Submit'}
              </Button>
            </Fieldset>
          </Form>
        </StyledMainAreaContainer>
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(KYCPending);
